import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Select, message } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import type { StockSearchBrief } from "../../global/GlobalType";
import { UserWatchlistApi } from "./UserWatchlistApi";
import type { UserWatchlistGroupDTO } from "./UserWatchlistType";
import { WatchlistCreateGroupModal } from "./WatchlistCreateGroupModal";
import "./UserHoldingsPage.css";

const RECT_BTN = "user-holdings-rect-modal-btn";
const ADD_WATCHLIST_GROUP_VALUE = "__uh_add_watchlist_group__";

/** 添加自选股：先选股，再选分组；分组列表末项为「添加分组」 */
export function AddWatchlistItemModal(props: {
  spaceId: string;
  open: boolean;
  groups: UserWatchlistGroupDTO[];
  /** 打开弹窗时默认选中的分组（通常为自选 Tab 当前选中的分组） */
  defaultSelectedGroupId: string;
  /** 打开时预填标的（如策略页当前个股） */
  seedStock?: StockSearchBrief | null;
  onRefreshGroups: () => void | Promise<void>;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
  /** 为 true 时不 toast「已加入自选」（由调用方自定义成功反馈） */
  silentSuccess?: boolean;
}): JSX.Element {
  const {
    spaceId,
    open,
    groups,
    defaultSelectedGroupId,
    seedStock,
    onRefreshGroups,
    onOpenNoteImport,
    onClose,
    silentSuccess,
  } = props;
  const [picked, setPicked] = useState<StockSearchBrief | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [reason, setReason] = useState("");
  const [newGroupModalOpen, setNewGroupModalOpen] = useState(false);
  const [refreshingGroups, setRefreshingGroups] = useState(false);
  const addWatchOpenWasOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = addWatchOpenWasOpenRef.current;
    addWatchOpenWasOpenRef.current = open;

    if (!open) {
      setNewGroupModalOpen(false);
      return;
    }

    // 仅在关闭→打开时重置表单并带入默认分组；弹窗打开期间 groups 刷新不改变已选分组
    if (!wasOpen) {
      setReason("");
      setNewGroupModalOpen(false);
      if (seedStock?.code?.trim()) {
        setPicked(seedStock);
      } else {
        setPicked(null);
      }
      const prefer = String(defaultSelectedGroupId ?? "").trim();
      const inList =
        prefer && groups.some((g) => String(g.id ?? "") === prefer) ? prefer : "";
      setSelectedGroupId(inList);
    }
  }, [open, defaultSelectedGroupId, groups, seedStock]);

  const handleRefreshGroups = async () => {
    setRefreshingGroups(true);
    try {
      await onRefreshGroups();
    } finally {
      setRefreshingGroups(false);
    }
  };

  const submit = async () => {
    const gid = String(selectedGroupId ?? "").trim();
    if (!gid) {
      message.error("请选择分组");
      throw new Error("cancel");
    }
    if (!picked?.code) {
      message.error("请选择标的");
      throw new Error("cancel");
    }
    const rs = reason.trim();
    if (!rs) {
      message.warning("请填写添加理由");
      throw new Error("cancel");
    }
    const ok = await UserWatchlistApi.addItem(spaceId, {
      groupId: gid,
      stockCode: picked.code,
      stockName: picked.name,
      addReason: rs.slice(0, 50),
    });
    if (ok) {
      if (!silentSuccess) {
        message.success("已加入自选");
      }
      onClose(true);
    } else {
      message.error("添加失败（可能已在该分组中）");
      throw new Error("cancel");
    }
  };

  const groupSelectOptions = useMemo(
    () => [
      ...groups.map((g) => ({
        label: g.groupName ?? "—",
        value: String(g.id ?? ""),
      })),
      {
        label: (
          <span className="uh-watchlist-add-group-option">
            <PlusOutlined />
            <span>添加分组</span>
          </span>
        ),
        value: ADD_WATCHLIST_GROUP_VALUE,
      },
    ],
    [groups]
  );

  return (
    <>
      <Modal
        open={open}
        title="添加自选"
        onCancel={() => onClose(false)}
        width={620}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
        cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
        onOk={() => submit()}
        className="uh-add-watch-modal"
        destroyOnClose
      >
        <div className="user-holding-form-block">
          <SearchStockWithCatalog
            stockCode={picked?.code}
            onSelect={(s) => setPicked(s)}
            onCleared={() => setPicked(null)}
          />
        </div>
        <div className="user-holding-form-block" style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
            }}
          >
            <Select
              className="user-holding-input uh-watchlist-modal-group-select"
              placeholder="请选择分组"
              options={groupSelectOptions}
              value={selectedGroupId || undefined}
              onChange={(v) => {
                const s = String(v ?? "");
                if (s === ADD_WATCHLIST_GROUP_VALUE) {
                  setNewGroupModalOpen(true);
                  return;
                }
                setSelectedGroupId(s);
              }}
              allowClear={false}
              style={{ flex: 1, minWidth: 0 }}
              popupClassName="uh-add-watch-modal-select-dropdown"
            />
            <Button
              type="default"
              className={RECT_BTN}
              style={{ width: 40, height: 32, padding: 0, flexShrink: 0 }}
              icon={<ReloadOutlined />}
              loading={refreshingGroups}
              aria-label="刷新分组列表"
              onClick={() => void handleRefreshGroups()}
            />
          </div>
        </div>
        <div className="user-holding-form-block" style={{ marginTop: 16 }}>
          <div className="uh-reason-detail-head">
            <span className="user-holding-lab">添加理由</span>
            {picked?.code && onOpenNoteImport ? (
              <button
                type="button"
                className="uh-reason-import-btn"
                onClick={() =>
                  onOpenNoteImport(picked.code, picked.name ?? "", (content) =>
                    setReason(content.slice(0, 50))
                  )
                }
              >
                从笔记中导入
              </button>
            ) : null}
          </div>
          <div className="uh-watchlist-reason-field">
            <Input.TextArea
              placeholder="添加理由，最多50个字"
              maxLength={50}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="user-holding-input uh-watchlist-reason-textarea"
            />
            <span className="uh-watchlist-reason-count">{reason.length}/50</span>
          </div>
        </div>
      </Modal>

      <WatchlistCreateGroupModal
        open={newGroupModalOpen}
        spaceId={spaceId}
        onClose={() => setNewGroupModalOpen(false)}
        onCreated={(id) => setSelectedGroupId(id)}
        onAfterListRefresh={onRefreshGroups}
      />
    </>
  );
}
